/**
 * TrapSituationAnalyzer Class
 *
 * Analyzes trap situations according to game rules
 * Following Architecture Agent pattern: < 250 lines, SOLID principles
 * Role: Game Logic Agent
 *
 * Three trap situations:
 * I. ESCAPABLE - Player has moves and neutral territories available
 * II. FORCE_BASED - No moves or territories, resolved by force calculation
 * III. SINGLE_COLOR - Surrounded by single color, instant capture
 *
 * Responsibilities:
 * - Detect trap type
 * - Calculate surrounding forces
 * - Determine resolution method
 */
import { MovementValidator } from '../MovementValidator';
export var TrapSituationType;
(function (TrapSituationType) {
    TrapSituationType["ESCAPABLE"] = "escapable";
    TrapSituationType["FORCE_BASED"] = "force";
    TrapSituationType["SINGLE_COLOR"] = "monopoly";
})(TrapSituationType || (TrapSituationType = {}));
export class TrapSituationAnalyzer {
    board;
    movementValidator;
    constructor(board) {
        this.board = board;
        this.movementValidator = new MovementValidator(board);
    }
    /**
     * Analyze trap situation for a position
     */
    analyzeTrap(position, sideId, round, movesRemaining) {
        // Check if position is actually trapped
        const trapInfo = this.movementValidator.getTrapInfo(position, sideId, round, movesRemaining);
        if (!trapInfo || !trapInfo.isTrapped) {
            return null;
        }
        // Determine surrounding sides
        const surroundingSides = trapInfo.surroundingSides || [];
        const uniqueSides = [...new Set(surroundingSides)];
        // Check for single color monopoly (Situation III)
        if (uniqueSides.length === 1 && uniqueSides[0] !== undefined) {
            return {
                type: TrapSituationType.SINGLE_COLOR,
                trappedPosition: position,
                trappedSide: sideId,
                surroundingSides: uniqueSides,
                dominantSide: uniqueSides[0],
                canEscape: false,
                availableEscapeRoutes: []
            };
        }
        // Check if player can escape (Situation I)
        const side = this.board.getSide(sideId);
        if (!side) {
            return null;
        }
        const hasMovesLeft = movesRemaining > 0;
        const availableRoutes = this.findEscapeRoutes(position, sideId);
        if (hasMovesLeft && availableRoutes.length > 0) {
            return {
                type: TrapSituationType.ESCAPABLE,
                trappedPosition: position,
                trappedSide: sideId,
                surroundingSides: uniqueSides,
                dominantSide: null,
                canEscape: true,
                availableEscapeRoutes: availableRoutes
            };
        }
        // Force-based resolution (Situation II)
        const forcePriority = this.calculateForcePriority(position, uniqueSides);
        return {
            type: TrapSituationType.FORCE_BASED,
            trappedPosition: position,
            trappedSide: sideId,
            surroundingSides: uniqueSides,
            dominantSide: this.getDominantSide(forcePriority),
            canEscape: false,
            availableEscapeRoutes: [],
            forcePriority
        };
    }
    /**
     * Find available escape routes
     */
    findEscapeRoutes(position, sideId) {
        const availableMoves = this.movementValidator.getAvailableMovesFrom(position);
        const escapeRoutes = [];
        for (const move of availableMoves) {
            const territory = this.board.getTerritoryAt(move);
            // Can only escape to neutral territories
            if (territory && territory.owner === null) {
                // Check if this position is not also trapped
                const moveNeighbors = move.getOrthogonalNeighbors();
                let isSafe = false;
                for (const neighbor of moveNeighbors) {
                    const neighborTerritory = this.board.getTerritoryAt(neighbor);
                    if (!neighborTerritory || neighborTerritory.owner === null ||
                        neighborTerritory.owner === sideId) {
                        isSafe = true;
                        break;
                    }
                }
                if (isSafe) {
                    escapeRoutes.push(move);
                }
            }
        }
        return escapeRoutes;
    }
    /**
     * Calculate force priority for surrounding sides
     */
    calculateForcePriority(position, surroundingSides) {
        const forcePriority = new Map();
        // Initialize forces
        for (const sideId of surroundingSides) {
            forcePriority.set(sideId, 0);
        }
        // 1. Orthogonal adjacency (highest priority)
        const orthogonalNeighbors = position.getOrthogonalNeighbors();
        for (const neighbor of orthogonalNeighbors) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (territory && territory.owner !== null) {
                const currentForce = forcePriority.get(territory.owner) || 0;
                forcePriority.set(territory.owner, currentForce + 100); // High weight
            }
        }
        // 2. Diagonal adjacency (medium priority)
        const diagonalNeighbors = position.getDiagonalNeighbors();
        for (const neighbor of diagonalNeighbors) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (territory && territory.owner !== null) {
                const currentForce = forcePriority.get(territory.owner) || 0;
                forcePriority.set(territory.owner, currentForce + 50); // Medium weight
            }
        }
        // 3. Global board presence (lowest priority)
        for (const sideId of surroundingSides) {
            const side = this.board.getSide(sideId);
            if (side) {
                const currentForce = forcePriority.get(sideId) || 0;
                const globalPresence = side.territories.size;
                forcePriority.set(sideId, currentForce + globalPresence);
            }
        }
        return forcePriority;
    }
    /**
     * Get dominant side from force calculation
     */
    getDominantSide(forcePriority) {
        let maxForce = 0;
        let dominantSide = null;
        let hasEqualForces = false;
        for (const [sideId, force] of forcePriority) {
            if (force > maxForce) {
                maxForce = force;
                dominantSide = sideId;
                hasEqualForces = false;
            }
            else if (force === maxForce) {
                hasEqualForces = true;
            }
        }
        // Full parity = burned land
        if (hasEqualForces) {
            return null;
        }
        return dominantSide;
    }
    /**
     * Check if trap would result in burned land
     */
    isBurnedLand(forcePriority) {
        if (forcePriority.size < 2)
            return false;
        const forces = Array.from(forcePriority.values());
        const maxForce = Math.max(...forces);
        const tiedForces = forces.filter(f => f === maxForce);
        return tiedForces.length > 1;
    }
    /**
     * Get trap description for UI
     */
    getTrapDescription(analysis) {
        switch (analysis.type) {
            case TrapSituationType.SINGLE_COLOR:
                return `Surrounded by single color - instant capture by Side ${analysis.dominantSide}`;
            case TrapSituationType.ESCAPABLE:
                return `Trapped but can escape - ${analysis.availableEscapeRoutes.length} escape routes available`;
            case TrapSituationType.FORCE_BASED:
                if (analysis.dominantSide === null) {
                    return 'Full parity - territory becomes burned land';
                }
                return `Cannot escape - territory goes to Side ${analysis.dominantSide} (strongest force)`;
            default:
                return 'Unknown trap situation';
        }
    }
}
//# sourceMappingURL=TrapSituationAnalyzer.js.map